import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { REVIEW_RESULT_VIEW, useSelectedReviewResultView } from 'reducers/reviewResultSlice';
import { useReviewPreviewPageStatus, useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import useReviewResultParams from 'screens/performanceReview/hooks/useReviewResultParams';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getBehaviorForm, getBehaviorFormV1, getReviewResponseResultV1 } from 'services/performanceReview';

const getData = async ({
  reviewId,
  employeeId,
  providerId,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) => {
  try {
    const res = await getBehaviorForm({
      reviewId,
      employeeId,
      providerId,
      responseId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });
    return res;
  } catch (err) {
    console.error('Error fetching competency form data : ', err);
  }
  return null;
};

const getDataV1 = async ({ reviewId, employeeId, providerType, isPreview, previewSubmissionStatus }) => {
  try {
    const res = await getBehaviorFormV1({
      reviewId,
      employeeId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });
    return res;
  } catch (err) {
    console.error('Error fetching competency form data : ', err);
  }
  return null;
};

export const useCompetencyResultData = ({ reviewId, employeeId, providerId, responseId, providerType }) => {
  const selectedView = useSelectedReviewResultView();
  const { isPreview, providerType: previewResponseProviderType } = useReviewPreviewParams();
  const previewSubmissionStatus = useReviewPreviewPageStatus();
  // for anonymous we use reviewId instead of providerId
  return useQuery(
    [REACT_QUERY_KEYS.ReviewCompetencyResultData, reviewId, employeeId, providerId, responseId],
    () => {
      if (selectedView === REVIEW_RESULT_VIEW.PEOPLE) {
        return getData({
          reviewId,
          employeeId,
          providerId: responseId ? null : providerId,
          responseId,
          // in case of view by people, we need to use the provider type for specific reviewer
          providerType,
          isPreview,
          previewSubmissionStatus,
        });
      }
      return getDataV1({
        reviewId,
        employeeId,
        // but in case of preview, we need to use the provider type from preview query params
        providerType: previewResponseProviderType,
        isPreview,
        previewSubmissionStatus,
      });
    },
    { enabled: !!employeeId }
  );
};

export const useCompetencyFormMutation = ({ answerQuestion }) => {
  const queryClient = useQueryClient();
  const { employeeId, providerId, reviewId, responseId } = useReviewResultParams();

  return useMutation(answerQuestion, {
    onMutate: async ({ feedbackText, feedbackTextHtml, selectedAnswerOptions, competencyIndex, questionIndex }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewCompetencyFormData, reviewId, employeeId, providerId, responseId],
      });

      // Snapshot the previous value
      const questionData = queryClient.getQueryData([
        REACT_QUERY_KEYS.ReviewCompetencyFormData,
        reviewId,
        employeeId,
        providerId,
        responseId,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.ReviewCompetencyFormData, reviewId, employeeId, providerId, responseId],
        (old) => {
          const updatedData = produce(old, (draft) => {
            // eslint-disable-next-line no-param-reassign
            draft.competencyQuestions[competencyIndex].questions[questionIndex] = {
              ...draft.competencyQuestions[competencyIndex]?.questions[questionIndex],
              feedbackText,
              feedbackTextHtml,
              selectedAnswerOptions,
            };
          });
          return updatedData;
        }
      );

      // Return a context object with the snapshotted value
      return { questionData };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newQuestion, context) => {
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.ReviewCompetencyFormData, reviewId, employeeId, providerId, responseId],
        context.questionData
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewCompetencyFormData, reviewId, employeeId, providerId, responseId],
      });
      // used to refetch sidebar data
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId],
      });
    },
  });
};

export const useCompetencyFormQueryData = () => {
  const queryClient = useQueryClient();
  const { employeeId, providerId, reviewId, responseId } = useReviewResultParams();

  const questionData = queryClient.getQueryData([
    REACT_QUERY_KEYS.ReviewCompetencyFormData,
    reviewId,
    employeeId,
    providerId,
    responseId,
  ]);

  return questionData;
};

const getResponseDataV1 = async ({
  employeeId,
  reviewId,
  goalId,
  competencyId,
  questionId,
  excludeEmployeeId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) => {
  try {
    const res = await getReviewResponseResultV1({
      employeeId,
      reviewId,
      goalId,
      competencyId,
      questionId,
      excludeEmployeeId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });
    return res;
  } catch (err) {
    console.error('Error fetching competency form response data : ', err);
  }
  return null;
};

export const useReviewResponseCompetencyData = ({ goalId, competencyId, questionId, excludeEmployeeId }) => {
  const { employeeId, reviewId } = useReviewFormParams();
  const { isPreview, providerType } = useReviewPreviewParams();
  const previewSubmissionStatus = useReviewPreviewPageStatus();

  return useQuery(
    [
      REACT_QUERY_KEYS.ReviewResponseCompetencyResultData,
      employeeId,
      reviewId,
      goalId,
      competencyId,
      questionId,
      excludeEmployeeId,
    ],
    () =>
      getResponseDataV1({
        goalId,
        competencyId,
        reviewId,
        employeeId,
        questionId,
        excludeEmployeeId,
        providerType,
        isPreview,
        previewSubmissionStatus,
      })
  );
};
